import { useState } from 'react';
import { Edit, Trash2, Check, X, Gift, DollarSign, Printer, Receipt, ChefHat, RotateCcw, Ban, RefreshCcw, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import ReceiptPreview from './ReceiptPreview';
import KitchenTicketPreview from './KitchenTicketPreview';

import { api } from '@/utils/apiClient';

const orderStatuses = ['pending', 'confirmed', 'preparing', 'ready', 'completed', 'cancelled'];

// Cancellation/Refund reasons
const CANCEL_REASONS = [
  "Customer requested cancellation",
  "Order mistake by staff",
  "Item out of stock",
  "Kitchen issue",
  "Customer no-show",
  "Payment issue",
  "Duplicate order",
  "Other"
];

const REFUND_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card (Original Payment)" },
  { value: "mpaisa", label: "M-PAiSA" },
  { value: "store_credit", label: "Store Credit" }
];
const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  preparing: 'bg-purple-100 text-purple-800',
  ready: 'bg-green-100 text-green-800',
  completed: 'bg-gray-100 text-gray-600',
  cancelled: 'bg-red-100 text-red-800',
  comped: 'bg-purple-100 text-purple-800'
};

export default function OrdersTab({ orders, onRefresh }) {
  const [filter, setFilter] = useState('all');
  const [editingOrder, setEditingOrder] = useState(null);
  const [editForm, setEditForm] = useState({});
  
  // Comp order state
  const [showCompModal, setShowCompModal] = useState(null);
  const [compReason, setCompReason] = useState('');
  const [compNotes, setCompNotes] = useState('');
  const [comping, setComping] = useState(false);
  
  // Print modal state
  const [showReceiptModal, setShowReceiptModal] = useState(null);
  const [showKitchenModal, setShowKitchenModal] = useState(null);
  const [isReprint, setIsReprint] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  
  // Cancel/Refund modal state
  const [showCancelModal, setShowCancelModal] = useState(null);
  const [showRefundModal, setShowRefundModal] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNotes, setCancelNotes] = useState('');
  const [refundMethod, setRefundMethod] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [isPartialRefund, setIsPartialRefund] = useState(false);
  const [processing, setProcessing] = useState(false);

  const filteredOrders = filter === 'all' 
    ? orders 
    : filter === 'comped'
    ? orders.filter(o => o.is_comped)
    : orders.filter(o => o.status === filter);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/admin/orders/${id}/status`, { status });
      toast.success('Status updated');
      
      // Auto-route to stations when order is confirmed
      if (status === 'confirmed') {
        try {
          const routeRes = await api.post(`/receipts/route-order/${id}`);
          const stations = routeRes.data.stations || [];
          if (stations.length > 0) {
            toast.success(`Tickets sent to: ${stations.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')}`);
          }
        } catch (routeErr) {
          // Don't fail the status update if routing fails
          console.error('Failed to route order:', routeErr);
        }
      }
      
      onRefresh();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleEdit = (order) => {
    setEditingOrder(order.id);
    setEditForm({
      customer_name: order.customer_name,
      customer_email: order.customer_email,
      customer_phone: order.customer_phone,
      pickup_time: order.pickup_time || '',
      special_instructions: order.special_instructions || ''
    });
  };

  const handleSaveEdit = async (orderId) => {
    try {
      await api.put(`/admin/orders/${orderId}`, editForm);
      toast.success('Order updated');
      setEditingOrder(null);
      onRefresh();
    } catch (err) {
      toast.error('Failed to update order');
    }
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;
    try {
      await api.delete(`/admin/orders/${orderId}`);
      toast.success('Order deleted');
      onRefresh();
    } catch (err) {
      toast.error('Failed to delete order');
    }
  };

  const handleCompOrder = async () => {
    if (!compReason.trim()) {
      toast.error('Please provide a reason for comping this order');
      return;
    }
    
    setComping(true);
    try {
      const res = await api.post(`/orders/${showCompModal.id}/comp`, { 
        reason: compReason,
        notes: compNotes || null
      });
      
      toast.success(`Order comped by ${res.data.comped_by}`);
      setShowCompModal(null);
      setCompReason('');
      setCompNotes('');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to comp order');
    }
    setComping(false);
  };

  // Print functions
  const handlePrintReceipt = (order, reprint = false) => {
    setIsReprint(reprint);
    setShowReceiptModal(order.id);
  };

  const handlePrintKitchenTicket = (order, reprint = false, cancelled = false) => {
    setIsReprint(reprint);
    setIsCancelled(cancelled);
    setShowKitchenModal(order.id);
  };

  // Route order to all 3 stations (Kitchen, Juicer, Barista)
  const handlePrintAllStations = async (order) => {
    try {
      toast.info('Routing order to all stations...');
      const res = await api.post(`/receipts/route-order/${order.id}`);
      const stations = res.data.stations || [];
      if (stations.length > 0) {
        toast.success(`Tickets sent to: ${stations.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')}`);
      } else {
        toast.info('No station tickets needed for this order');
      }
    } catch (err) {
      toast.error('Failed to route order to stations');
    }
  };

  const handleCancelOrder = async (order) => {
    if (!window.confirm(`Cancel order #${order.id.slice(0, 8).toUpperCase()}? A cancellation notice will be sent to the kitchen.`)) {
      return;
    }
    
    try {
      await api.post(`/orders/${order.id}/cancel`);
      toast.success('Order cancelled and kitchen notified');
      // Show kitchen cancellation ticket
      handlePrintKitchenTicket(order, false, true);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel order');
    }
  };

  // Full Cancel with cascading (inventory, revenue, etc)
  const handleFullCancel = async () => {
    if (!cancelReason.trim()) {
      toast.error('Please select a reason for cancellation');
      return;
    }
    
    setProcessing(true);
    try {
      const res = await api.post(`/admin/orders/${showCancelModal.id}/cancel`, {
        reason: cancelReason,
        refund_method: refundMethod || null,
        notes: cancelNotes || null
      });
      
      toast.success('Order cancelled', {
        description: `Refund: $${res.data.refund_amount?.toFixed(2) || '0.00'}. ${res.data.inventory_restored?.length > 0 ? `Inventory restored for ${res.data.inventory_restored.length} items.` : ''}`
      });
      setShowCancelModal(null);
      resetCancelForm();
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel order');
    }
    setProcessing(false);
  };

  // Refund order (partial or full)
  const handleRefund = async () => {
    if (!cancelReason.trim()) {
      toast.error('Please select a reason for refund');
      return;
    }
    if (!refundMethod) {
      toast.error('Please select a refund method');
      return;
    }
    
    const amount = isPartialRefund && refundAmount ? parseFloat(refundAmount) : null;
    
    setProcessing(true);
    try {
      const res = await api.post(`/admin/orders/${showRefundModal.id}/refund`, {
        reason: cancelReason,
        refund_method: refundMethod,
        refund_amount: amount,
        notes: cancelNotes || null
      });
      
      toast.success(res.data.is_partial ? 'Partial refund processed' : 'Full refund processed', {
        description: `Refunded $${res.data.refund_amount?.toFixed(2)}${res.data.inventory_restored?.length > 0 ? `. Inventory restored.` : ''}`
      });
      setShowRefundModal(null);
      resetCancelForm();
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process refund');
    }
    setProcessing(false);
  };

  const resetCancelForm = () => {
    setCancelReason('');
    setCancelNotes('');
    setRefundMethod('');
    setRefundAmount('');
    setIsPartialRefund(false);
  };

  return (
    <div data-testid="orders-tab">
      <h1 className="font-heading text-3xl mb-8 text-foreground">Orders</h1>

      {/* Filter */}
      <div className="flex gap-2 mb-6 overflow-x-auto">
        {['all', ...orderStatuses, 'comped'].map(status => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              filter === status 
                ? 'bg-primary text-white' 
                : 'bg-card hover:bg-muted text-foreground border border-border'
            }`}
          >
            {status === 'comped' ? 'Comped/No-Charge' : status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Orders List */}
      <div className="space-y-4">
        {filteredOrders.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <p>No orders found</p>
          </div>
        ) : (
          filteredOrders.map(order => (
            <div key={order.id} className="bg-card rounded-2xl p-6 shadow-sm border border-border" data-testid={`order-${order.id}`}>
              {editingOrder === order.id ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Customer Name</Label>
                      <Input
                        value={editForm.customer_name}
                        onChange={(e) => setEditForm(prev => ({ ...prev, customer_name: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        value={editForm.customer_email}
                        onChange={(e) => setEditForm(prev => ({ ...prev, customer_email: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={editForm.customer_phone}
                        onChange={(e) => setEditForm(prev => ({ ...prev, customer_phone: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>Pickup Time</Label>
                      <Input
                        value={editForm.pickup_time}
                        onChange={(e) => setEditForm(prev => ({ ...prev, pickup_time: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Special Instructions</Label>
                    <Input
                      value={editForm.special_instructions}
                      onChange={(e) => setEditForm(prev => ({ ...prev, special_instructions: e.target.value }))}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => handleSaveEdit(order.id)} className="bg-primary">
                      <Check className="w-4 h-4 mr-1" /> Save
                    </Button>
                    <Button variant="outline" onClick={() => setEditingOrder(null)}>
                      <X className="w-4 h-4 mr-1" /> Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-lg text-foreground">{order.customer_name}</h3>
                        {order.order_type && (
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            order.order_type === 'kiosk' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                            order.order_type === 'online' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                            order.order_type === 'dine_in' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                            order.order_type === 'walk_in_takeaway' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                            'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
                          }`}>
                            {order.order_type === 'walk_in_takeaway' ? 'Walk-In' : 
                             order.order_type === 'dine_in' ? 'Dine In' :
                             order.order_type === 'online' ? 'Online' :
                             order.order_type === 'kiosk' ? 'Kiosk' : order.order_type}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{order.customer_email} • {order.customer_phone}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(order.created_at).toLocaleString()}
                        {order.pickup_time && ` • Pickup: ${order.pickup_time}`}
                      </p>
                    </div>
                    <div className="text-right">
                      {order.is_comped ? (
                        <>
                          <p className="font-heading text-xl text-muted-foreground line-through">${order.original_total?.toFixed(2) || order.total?.toFixed(2)}</p>
                          <p className="font-heading text-lg text-green-600">$0.00 (Comped)</p>
                        </>
                      ) : (
                        <p className="font-heading text-xl text-foreground">${order.total?.toFixed(2)}</p>
                      )}
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mt-1 ${
                        order.is_comped ? statusColors.comped : statusColors[order.status]
                      }`}>
                        {order.is_comped ? 'COMPED' : order.status}
                      </span>
                    </div>
                  </div>
                  
                  {/* Comp Info */}
                  {order.is_comped && (
                    <div className="mb-4 p-3 bg-purple-50 dark:bg-purple-500/10 rounded-lg border border-purple-200 dark:border-purple-500/20">
                      <div className="flex items-center gap-2 text-sm">
                        <Gift className="w-4 h-4 text-purple-600" />
                        <span className="font-medium text-purple-700 dark:text-purple-400">Comped by {order.comped_by_name || order.comped_by_email}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Reason: {order.comp_reason}</p>
                      {order.comp_notes && <p className="text-xs text-muted-foreground">Notes: {order.comp_notes}</p>}
                      <p className="text-xs text-muted-foreground">
                        {order.comped_at && new Date(order.comped_at).toLocaleString()}
                      </p>
                    </div>
                  )}
                  
                  <div className="border-t border-border pt-4 mb-4">
                    {order.items.map((item, i) => (
                      <div key={i} className="flex justify-between text-sm py-1 text-foreground">
                        <span>{item.name} x{item.quantity}</span>
                        <span>${(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    {order.status !== 'completed' && order.status !== 'cancelled' && !order.is_comped && (
                      <Select onValueChange={(value) => updateStatus(order.id, value)}>
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="Update status" />
                        </SelectTrigger>
                        <SelectContent>
                          {orderStatuses.map(status => (
                            <SelectItem key={status} value={status}>
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    
                    {/* Print Receipt Button */}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-green-600 hover:text-green-700 border-green-200"
                      onClick={() => handlePrintReceipt(order, false)}
                      data-testid={`print-receipt-${order.id}`}
                    >
                      <Receipt className="w-4 h-4 mr-1" /> Receipt
                    </Button>
                    
                    {/* Reprint Receipt Button */}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-blue-600 hover:text-blue-700 border-blue-200"
                      onClick={() => handlePrintReceipt(order, true)}
                      data-testid={`reprint-receipt-${order.id}`}
                    >
                      <RotateCcw className="w-4 h-4 mr-1" /> Reprint
                    </Button>
                    
                    {/* Print Kitchen Ticket Button */}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-orange-600 hover:text-orange-700 border-orange-200"
                      onClick={() => handlePrintKitchenTicket(order, false)}
                      data-testid={`print-kitchen-${order.id}`}
                    >
                      <ChefHat className="w-4 h-4 mr-1" /> Kitchen
                    </Button>
                    
                    {/* Print All Stations Button (3-Station POS) */}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-purple-600 hover:text-purple-700 border-purple-200"
                      onClick={() => handlePrintAllStations(order)}
                      data-testid={`print-all-stations-${order.id}`}
                    >
                      <Printer className="w-4 h-4 mr-1" /> All Stations
                    </Button>
                    
                    {/* Comp Order Button */}
                    {!order.is_comped && order.status !== 'cancelled' && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-purple-600 hover:text-purple-700 border-purple-200"
                        onClick={() => setShowCompModal(order)}
                        data-testid={`comp-order-${order.id}`}
                      >
                        <Gift className="w-4 h-4 mr-1" /> Comp
                      </Button>
                    )}
                    
                    {/* Cancel Order Button - Quick Cancel */}
                    {order.status !== 'completed' && order.status !== 'cancelled' && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-red-500 hover:text-red-600 border-red-200"
                        onClick={() => handleCancelOrder(order)}
                        data-testid={`quick-cancel-order-${order.id}`}
                      >
                        <Ban className="w-4 h-4 mr-1" /> Quick Cancel
                      </Button>
                    )}
                    
                    {/* Full Cancel with Cascading - Opens Modal */}
                    {order.status !== 'cancelled' && order.payment_status !== 'cancelled' && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-red-600 hover:text-red-700 border-red-300 bg-red-50/50 hover:bg-red-100/50 dark:bg-red-900/10 dark:hover:bg-red-900/20"
                        onClick={() => { setShowCancelModal(order); resetCancelForm(); }}
                        data-testid={`cancel-order-${order.id}`}
                      >
                        <X className="w-4 h-4 mr-1" /> Cancel & Refund
                      </Button>
                    )}
                    
                    {/* Refund Button (for completed orders) */}
                    {order.status === 'completed' && order.payment_status !== 'refunded' && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-amber-600 hover:text-amber-700 border-amber-300 bg-amber-50/50 hover:bg-amber-100/50 dark:bg-amber-900/10 dark:hover:bg-amber-900/20"
                        onClick={() => { setShowRefundModal(order); resetCancelForm(); }}
                        data-testid={`refund-order-${order.id}`}
                      >
                        <RefreshCcw className="w-4 h-4 mr-1" /> Refund
                      </Button>
                    )}
                    
                    <Button variant="outline" size="sm" onClick={() => handleEdit(order)} data-testid={`edit-order-${order.id}`}>
                      <Edit className="w-4 h-4 mr-1" /> Edit
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-500 hover:text-red-600" onClick={() => handleDelete(order.id)} data-testid={`delete-order-${order.id}`}>
                      <Trash2 className="w-4 h-4 mr-1" /> Delete
                    </Button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
      
      {/* Comp Order Modal */}
      <Dialog open={!!showCompModal} onOpenChange={() => setShowCompModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-purple-600" />
              Comp Order (No Charge)
            </DialogTitle>
            <DialogDescription>
              This will mark the order as comped and set the total to $0.00. This action is logged.
            </DialogDescription>
          </DialogHeader>
          
          {showCompModal && (
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{showCompModal.customer_name}</p>
                <p className="text-sm text-muted-foreground">{showCompModal.items?.length} items • Original: ${showCompModal.total?.toFixed(2)}</p>
              </div>
              
              <div className="space-y-1">
                <Label className="text-sm">Reason for Comp *</Label>
                <select
                  value={compReason}
                  onChange={(e) => setCompReason(e.target.value)}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="comp-reason-select"
                >
                  <option value="">Select a reason...</option>
                  <option value="Customer complaint - quality issue">Customer complaint - quality issue</option>
                  <option value="Customer complaint - long wait">Customer complaint - long wait</option>
                  <option value="Order mistake by staff">Order mistake by staff</option>
                  <option value="VIP customer">VIP customer</option>
                  <option value="Staff meal">Staff meal</option>
                  <option value="Promotional giveaway">Promotional giveaway</option>
                  <option value="Manager discretion">Manager discretion</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-sm">Additional Notes (optional)</Label>
                <Input
                  placeholder="Any additional details..."
                  value={compNotes}
                  onChange={(e) => setCompNotes(e.target.value)}
                  data-testid="comp-notes-input"
                />
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleCompOrder}
                  disabled={comping || !compReason}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  data-testid="confirm-comp-btn"
                >
                  {comping ? 'Processing...' : 'Confirm Comp'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setShowCompModal(null); setCompReason(''); setCompNotes(''); }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Receipt Preview Modal */}
      <Dialog open={!!showReceiptModal} onOpenChange={() => setShowReceiptModal(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-green-600" />
              {isReprint ? 'Reprint Customer Receipt' : 'Customer Receipt'}
            </DialogTitle>
          </DialogHeader>
          {showReceiptModal && (
            <ReceiptPreview 
              orderId={showReceiptModal} 
              isReprint={isReprint}
              onClose={() => setShowReceiptModal(null)}
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Kitchen Ticket Preview Modal */}
      <Dialog open={!!showKitchenModal} onOpenChange={() => setShowKitchenModal(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ChefHat className={`w-5 h-5 ${isCancelled ? 'text-red-600' : 'text-orange-600'}`} />
              {isCancelled ? 'Cancellation Notice - Kitchen' : isReprint ? 'Reprint Kitchen Ticket' : 'Kitchen Ticket'}
            </DialogTitle>
          </DialogHeader>
          {showKitchenModal && (
            <KitchenTicketPreview 
              orderId={showKitchenModal} 
              isReprint={isReprint}
              isCancelled={isCancelled}
              onClose={() => setShowKitchenModal(null)}
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Cancel Order Modal (Full Cascading) */}
      <Dialog open={!!showCancelModal} onOpenChange={() => { setShowCancelModal(null); resetCancelForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Cancel Order & Process Refund
            </DialogTitle>
            <DialogDescription>
              This will cancel the order, restore inventory, update revenue reports, and log the cancellation.
            </DialogDescription>
          </DialogHeader>
          
          {showCancelModal && (
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-muted rounded-lg">
                <div className="flex justify-between">
                  <div>
                    <p className="font-medium">{showCancelModal.customer_name}</p>
                    <p className="text-sm text-muted-foreground">{showCancelModal.items?.length} items</p>
                  </div>
                  <p className="font-heading text-xl">${showCancelModal.total?.toFixed(2)}</p>
                </div>
              </div>
              
              <div className="space-y-1">
                <Label className="text-sm">Reason for Cancellation *</Label>
                <select
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="cancel-reason-select"
                >
                  <option value="">Select a reason...</option>
                  {CANCEL_REASONS.map(reason => (
                    <option key={reason} value={reason}>{reason}</option>
                  ))}
                </select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-sm">Refund Method (optional)</Label>
                <select
                  value={refundMethod}
                  onChange={(e) => setRefundMethod(e.target.value)}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="refund-method-select"
                >
                  <option value="">Not refunding yet</option>
                  {REFUND_METHODS.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-sm">Additional Notes (optional)</Label>
                <Input
                  placeholder="Any additional details..."
                  value={cancelNotes}
                  onChange={(e) => setCancelNotes(e.target.value)}
                  data-testid="cancel-notes-input"
                />
              </div>
              
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <p className="text-sm text-red-700 dark:text-red-400">
                  <strong>This action will:</strong>
                </p>
                <ul className="text-xs text-red-600 dark:text-red-400 mt-1 space-y-1 list-disc list-inside">
                  <li>Mark order as cancelled (excluded from revenue)</li>
                  <li>Restore inventory for all items</li>
                  <li>Update display counts</li>
                  <li>Remove from customer order history</li>
                  <li>Log cancellation with your name</li>
                </ul>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleFullCancel}
                  disabled={processing || !cancelReason}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  data-testid="confirm-cancel-btn"
                >
                  {processing ? 'Processing...' : 'Confirm Cancellation'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setShowCancelModal(null); resetCancelForm(); }}
                >
                  Back
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Refund Order Modal */}
      <Dialog open={!!showRefundModal} onOpenChange={() => { setShowRefundModal(null); resetCancelForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <RefreshCcw className="w-5 h-5" />
              Process Refund
            </DialogTitle>
            <DialogDescription>
              Refund this completed order. Partial refunds will keep the order active; full refunds will mark it as cancelled.
            </DialogDescription>
          </DialogHeader>
          
          {showRefundModal && (
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-muted rounded-lg">
                <div className="flex justify-between">
                  <div>
                    <p className="font-medium">{showRefundModal.customer_name}</p>
                    <p className="text-sm text-muted-foreground">{showRefundModal.items?.length} items</p>
                  </div>
                  <p className="font-heading text-xl">${showRefundModal.total?.toFixed(2)}</p>
                </div>
              </div>
              
              <div className="space-y-1">
                <Label className="text-sm">Reason for Refund *</Label>
                <select
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="refund-reason-select"
                >
                  <option value="">Select a reason...</option>
                  {CANCEL_REASONS.map(reason => (
                    <option key={reason} value={reason}>{reason}</option>
                  ))}
                </select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-sm">Refund Method *</Label>
                <select
                  value={refundMethod}
                  onChange={(e) => setRefundMethod(e.target.value)}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="refund-method-select-refund"
                >
                  <option value="">Select refund method...</option>
                  {REFUND_METHODS.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="partial-refund"
                  checked={isPartialRefund}
                  onChange={(e) => setIsPartialRefund(e.target.checked)}
                  className="rounded"
                />
                <Label htmlFor="partial-refund" className="text-sm cursor-pointer">Partial refund</Label>
              </div>
              
              {isPartialRefund && (
                <div className="space-y-1">
                  <Label className="text-sm">Refund Amount ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={showRefundModal.total}
                    placeholder={`Max: $${showRefundModal.total?.toFixed(2)}`}
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    data-testid="refund-amount-input"
                  />
                </div>
              )}
              
              <div className="space-y-1">
                <Label className="text-sm">Additional Notes (optional)</Label>
                <Input
                  placeholder="Any additional details..."
                  value={cancelNotes}
                  onChange={(e) => setCancelNotes(e.target.value)}
                  data-testid="refund-notes-input"
                />
              </div>
              
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  <strong>Refund Amount:</strong> ${isPartialRefund && refundAmount ? parseFloat(refundAmount).toFixed(2) : showRefundModal.total?.toFixed(2)}
                </p>
                {!isPartialRefund && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    Full refund will restore inventory and mark order as cancelled.
                  </p>
                )}
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleRefund}
                  disabled={processing || !cancelReason || !refundMethod}
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  data-testid="confirm-refund-btn"
                >
                  {processing ? 'Processing...' : 'Process Refund'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setShowRefundModal(null); resetCancelForm(); }}
                >
                  Back
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export { orderStatuses, statusColors };
